import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { useAddUsersToTeam } from '../../../../../hooks/api';
import { fetchAddableMembers, getAddableMembersQueryKey } from '../../../../../hooks/api/teams/useAddableMembers';
import { queryClient } from '../../../../../ReactQuery';
import { FormGroup, FormLabel, LabeledSelect } from '../../../../generic';
import Button, { ButtonGroup, LoaderButton } from '../../../../generic/Button';
import AsyncPaginatedSelect from '../../../../generic/Select/AsyncPaginatedSelect';
import useSelectRoles from '../hooks/useSelectRoles';
import type { SelectOption } from '../../../../../types';

import { type AddableUser, AddUserOption } from './AddUserOption';

const BUILT_IN_TEAM_VIEWER_ROLE_ID = 6;

type Props = {
  teamId: number;
  teamName: string;
  onHide: () => void;
};

/**
 * The modal for adding users to a team. It will fetch users that are not already in the team (using the useAddableMembers query)
 * and the roles (using the new `useSelectRoles` hook). The user will then be able to select the users they want to add
 * and the default role they want to set for them on the team.
 *
 * @param {Props} props - The props for the component.
 * @param {number} props.teamId - The ID of the team to add users to.
 * @param {string} props.teamName - The name of the team to add users to.
 * @param {() => void} props.onHide - The callback to hide the modal.
 * @returns {VoidFunctionComponent} The component.
 */
const AddMembersModal: VoidFunctionComponent<Props> = ({ teamId, teamName, onHide }) => {
  //  State:
  const [selectedRole, setSelectedRole] = useState<SelectOption<number> | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<AddableUser[]>([]);
  //  Data fetching
  const { roleOptions, isLoading: isLoadingRoles } = useSelectRoles({
    selectedRoleId: BUILT_IN_TEAM_VIEWER_ROLE_ID,
    teamId,
  });

  const isLoadingAny = isLoadingRoles;

  const loadPage = async (searchString: string, _loaded: unknown, additional: { page: number } | undefined) => {
    const res = await queryClient.ensureQueryData({
      queryKey: getAddableMembersQueryKey(teamId, 20, searchString),
      queryFn: () => fetchAddableMembers({ pageParam: additional?.page ?? 0 }, teamId, 20, searchString),
    });

    return {
      options:
        res.data?.results.map((user) => ({
          label: user.username || user.email || `user id: ${user.id}`,
          value: user.id,
          isServiceAccount: user.isServiceAccount,
        })) ?? [],
      hasMore: (res.data?.totalCount ?? 0) > (res.data?.page ?? 0) * (res.data?.pageSize ?? 0),
      additional: {
        page: (additional?.page ?? 0) + 1,
      },
    };
  };

  const { mutateAsync: addUsersToTeam, isLoading: isAddingUsersToTeam } = useAddUsersToTeam();

  const handleSave = async () => {
    await addUsersToTeam({
      teamId,
      users: selectedUsers.map((user) => ({ id: user.value, roleId: selectedRole?.value })),
    });
    onHide();
    //  Reset the state:
    setSelectedRole(null);
    setSelectedUsers([]);
  };

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title className="margin-bottom-sm">Add Members to Team &quot;{teamName}&quot;</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormGroup>
          <FormLabel label="Select Users">
            <AsyncPaginatedSelect
              isMulti
              onChange={setSelectedUsers}
              loadOptions={loadPage}
              value={selectedUsers}
              additional={{ page: 1 }}
              debounceTimeout={200}
              components={{
                Option: AddUserOption,
              }}
            />
          </FormLabel>
          <LabeledSelect
            label="Team Role"
            id="defaultRoleId"
            options={roleOptions}
            onChange={setSelectedRole}
            value={selectedRole}
            isDisabled={isLoadingRoles}
            placeholder="Select the role for these users on the team"
          />
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button type="secondary" size="medium" onClick={onHide} isDisabled={isLoadingAny}>
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            size="medium"
            onClick={handleSave}
            isDisabled={isLoadingAny || selectedUsers.length === 0 || !selectedRole}
            isLoading={isAddingUsersToTeam}
          >
            {selectedUsers.length > 0 ? `Add ${selectedUsers.length} Users` : 'Add Users'}
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default AddMembersModal;
